import React, { useContext, useEffect } from 'react'

import { useRemoteStrings } from '@acorns/web-utils'
import { useQuery } from '@apollo/react-hooks'
import { compose } from 'recompose'

import { Accessibility } from 'components/Accessibility'
import CenteredSpinner from 'components/CenteredSpinner'
import withReferralDisclosureRequired, {
  ReferralProps,
} from 'components/HOCs/WithReferralDisclosureRequired'
import { ActionType, LinkedAccount, PageName, SpendAccount, User } from 'generated/types'
import { shouldTreatAsSoftFail } from 'pages/Funding/Checking/useFundingInitiatedPolling'
import { ScreenViewedEvent } from 'pages/MOC/Success/events'
import { TestIDs } from 'test/utils/testIds'
import trackEvent from 'utils/analytics/trackEvent'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getFlow } from 'utils/FlowProvider/utils'
import { FunnelContext } from 'utils/FunnelContext'
import withTieredRegistration, { WithTieredRegistrationProps } from 'utils/TieredRegistration'

import userQuery from '../../Success/gql/user.query.gql'
import MocSucessPersonImg from '../assets/moc-success-person.svg'
import MocSucessSignatureImg from '../assets/moc-success-signature.png'
import { Button } from './Button'
import {
  Background,
  ButtonContainer,
  LayoutContents,
  Message,
  MessageContainer,
  SignatureImage,
  SuccessIcon,
  TextContainer,
  Title,
} from './styled-components'

type Props = {
  ctaLabel: string
  handleNextClick: () => void
  header: string
  text: string
  secondaryCtaLabel: string
} & WithTieredRegistrationProps &
  WithFlowRouterProps &
  ReferralProps

const enhance = compose<Props, {}>(
  withFlowRouter,
  withTieredRegistration,
  withReferralDisclosureRequired(),
)

const MOCSuccess: React.FunctionComponent<Props> = ({
  route,
  referralDisclosureRequired,
  referralDisclosureRequiredLoading,
}) => {
  const { getRemoteString } = useRemoteStrings()
  const { isDynamicFunnelEnabled, routeToPage } = useContext(FunnelContext)
  const { data: userDetailsData, loading: isLoadingUserDetails } = useQuery<{
    user: User
    spendAccount: SpendAccount
    linkedAccounts: [LinkedAccount]
  }>(userQuery, { fetchPolicy: 'network-only' })

  useEffect(() => {
    trackEvent('Screen Viewed', {
      eventData: ScreenViewedEvent,
      flow: getFlow(),
    })
  }, [])

  useEffect(() => {
    if (!referralDisclosureRequiredLoading && referralDisclosureRequired) {
      isDynamicFunnelEnabled
        ? routeToPage(PageName.MocSuccess, ActionType.MoreInfo)
        : route({ from: FlowRoute.MOCSuccess, action: 'more-info' })
    }
  }, [referralDisclosureRequired, referralDisclosureRequiredLoading])

  if (isLoadingUserDetails || referralDisclosureRequiredLoading || referralDisclosureRequired) {
    return (
      <Background>
        <CenteredSpinner />
      </Background>
    )
  }

  const isSoftFail = shouldTreatAsSoftFail(userDetailsData)
  const assignTitle = () => {
    if (isSoftFail)
      return getRemoteString({ key: 'registration.moc_feature_lander.success.headline.soft_fail' })
    else if (userDetailsData?.user?.firstName) {
      return getRemoteString({
        key: 'registration.moc_feature_lander.success.headline.variable',
        variables: [userDetailsData.user.firstName],
      })
    }

    return getRemoteString({ key: 'registration.moc_feature_lander.success.headline' })
  }

  if (isLoadingUserDetails || referralDisclosureRequiredLoading || referralDisclosureRequired) {
    return (
      <Background>
        <CenteredSpinner />
      </Background>
    )
  }

  return (
    <Background>
      <Accessibility logoColor="white" zIndex={2} />
      <LayoutContents>
        <SuccessIcon src={MocSucessPersonImg} alt="Congrats!" />
        <TextContainer>
          <Title
            data-testid={isSoftFail ? TestIDs.SUCCESS.MOC.TITLE_ALT : TestIDs.SUCCESS.MOC.TITLE}
          >
            {assignTitle()}
          </Title>
          <MessageContainer>
            <Message
              data-testid={isSoftFail ? TestIDs.SUCCESS.MOC.BODY_ALT : TestIDs.SUCCESS.MOC.BODY}
            >
              {isSoftFail
                ? getRemoteString({
                    key: 'registration.moc_feature_lander.success.soft_fail_body',
                  })
                : getRemoteString({ key: 'registration.moc_feature_lander.success.body' })}
            </Message>
            <Message>
              {getRemoteString({
                key: 'registration.moc_feature_lander.success.signature.title',
              })}
            </Message>
          </MessageContainer>
          <SignatureImage src={MocSucessSignatureImg} />
        </TextContainer>
        <ButtonContainer>
          <Button />
        </ButtonContainer>
      </LayoutContents>
    </Background>
  )
}

export default enhance(MOCSuccess)
